/**
 * The embeddable give widget (GH-847): its address, its session, and its brand colour.
 *
 * Shared by three halves that ship separately — the console builds the address and the snippets,
 * the donor bundle reads the address back, the API mints the widget's session — so the shapes live
 * here once, the way `client-headers.ts` does for headers.
 *
 * **Any site may frame it** (owner, 2026-10-02, GH-850). The per-organization allow-list GH-847
 * built is gone; what keeps a hostile host from steering a donor is the give box itself — no token
 * in the address, two gestures before any charge, nothing accepted from the host, nothing about
 * the donor sent to it (`docs/threat-models/GH-847-embeddable-give-widget.md`).
 *
 * ## What the address may carry, and what it never carries
 *
 * `/embed/give/<organizationId>?event=&designation=&amount=&theme=&accent=&lang=` — public
 * identifiers and presentation only. **No signed token and no secret is ever in it**: the widget
 * asks the API for its checkout context after it loads (`POST /v1/public/embed/sessions`), so the
 * address pasted onto somebody else's page is safe to be seen, logged and copied. Every parameter
 * is a *hint*: `amount` preselects a chip and is priced by the server's quote like any other amount
 * the donor picks; `designation` is dropped unless the server's context offers it; nothing in the
 * address can start a payment.
 */
import { z } from "zod";
import { embedSessionWidgetSchema } from "./embed-widgets.js";
/** The path the widget is served at, for one organization. */
export const embedGivePath = (organizationId) => `/embed/give/${encodeURIComponent(organizationId)}`;
/** The popup a framed widget opens for Apple Pay (and wallets the frame cannot show). */
export const embedWalletPath = (organizationId) => `${embedGivePath(organizationId)}/wallet`;
/**
 * GH-877: the small first-party window a framed widget opens for a donor to sign in. A frame on
 * somebody else's site has partitioned storage and cannot complete an Apple or Google sign-in, so
 * the sign-in runs at top level on GiveHere and hands the session back to the box that opened it.
 * Never framable (the static server serves it with `frame-ancestors 'none'`).
 */
export const embedSignInPath = (organizationId) => `${embedGivePath(organizationId)}/sign-in`;
/**
 * The frame's permissions, the same in the console's iframe snippet and in `/embed.js` (whose
 * test reads this constant). `payment` for the Payment Request API (Google Pay, and Apple Pay on
 * Safari 17+ where usable). The sandbox is the least that still runs Stripe's card element and
 * 3-D Secure (scripts; the frame's own origin for its API and tab state; forms), and lets the box
 * open the GiveHere window Apple Pay needs, un-sandboxed. Never `allow-top-navigation`.
 */
export const EMBED_FRAME_ALLOW = "payment";
export const EMBED_FRAME_SANDBOX = "allow-scripts allow-same-origin allow-forms allow-popups allow-popups-to-escape-sandbox";
export const EMBED_THEMES = ["light", "dark"];
/** Languages written right to left, so the widget turns its layout with `lang`. */
const RTL_LANGUAGES = new Set(["ar", "fa", "he", "ur", "ps", "yi"]);
const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
/** Minor units, no leading zero, at most ten digits — the same shape `give-hints.ts` accepts. */
const AMOUNT = /^[1-9][0-9]{0,9}$/;
/** Six hex digits, no `#` (it would need escaping in every snippet). */
const ACCENT = /^[0-9a-f]{6}$/i;
const LANG = /^[a-z]{2,3}(-[A-Z]{2})?$/;
export const DEFAULT_EMBED_OPTIONS = Object.freeze({
    eventId: null,
    designationId: null,
    amountMinor: null,
    theme: "light",
    accent: null,
    lang: null,
});
/**
 * Read the widget's options from its query string. Anything malformed is dropped rather than
 * refused: a host page with a typo still gets a working give box, with the default in place of the
 * value it got wrong.
 */
export function parseEmbedOptions(search) {
    const params = new URLSearchParams(search);
    const event = params.get("event");
    const designation = params.get("designation");
    const amount = params.get("amount");
    const theme = params.get("theme");
    const accent = params.get("accent");
    const lang = params.get("lang");
    return {
        eventId: event && UUID.test(event) ? event.toLowerCase() : null,
        designationId: designation && UUID.test(designation) ? designation.toLowerCase() : null,
        amountMinor: amount && AMOUNT.test(amount) ? amount : null,
        theme: theme === "dark" ? "dark" : "light",
        accent: accent && ACCENT.test(accent) ? accent.toLowerCase() : null,
        lang: lang && LANG.test(lang) ? lang : null,
    };
}
/** The query string for these options, "" when every one is the default. Inverse of the parser. */
export function embedQuery(options) {
    const parsed = parseEmbedOptions(new URLSearchParams(Object.entries({
        event: options.eventId,
        designation: options.designationId,
        amount: options.amountMinor,
        theme: options.theme,
        accent: options.accent,
        lang: options.lang,
    }).filter((entry) => typeof entry[1] === "string")).toString());
    const query = new URLSearchParams();
    if (parsed.eventId)
        query.set("event", parsed.eventId);
    if (parsed.designationId)
        query.set("designation", parsed.designationId);
    if (parsed.amountMinor)
        query.set("amount", parsed.amountMinor);
    if (parsed.theme !== "light")
        query.set("theme", parsed.theme);
    if (parsed.accent)
        query.set("accent", parsed.accent);
    if (parsed.lang)
        query.set("lang", parsed.lang);
    const text = query.toString();
    return text ? `?${text}` : "";
}
/** Whether a language is written right to left — `ar`, `ar-EG`, `he`… */
export const isRtlLanguage = (lang) => lang !== null && RTL_LANGUAGES.has(lang.split("-")[0] ?? "");
/* ------------------------------------------------------------- the brand colour */
/** How an organization's brand colour is stored and sent: `#` and six lower-case hex digits. */
export const BRAND_COLOR_PATTERN = /^#[0-9a-f]{6}$/;
/**
 * One brand colour as a person types it — `#1C5B45`, `1c5b45`, with spaces around — as the stored
 * spelling `#1c5b45`, or null for anything that is not six hex digits. Three-digit shorthand,
 * names (`red`), `rgb()` and alpha are refused rather than guessed at: one spelling is stored, and
 * the console's colour picker only ever produces this one.
 */
export function normalizeBrandColor(value) {
    const trimmed = value.trim().toLowerCase();
    const digits = trimmed.startsWith("#") ? trimmed.slice(1) : trimmed;
    return /^[0-9a-f]{6}$/.test(digits) ? `#${digits}` : null;
}
/** The widget's `accent` hint (`rrggbb`, no `#`) for a stored brand colour, or null. */
export const brandColorAccent = (brandColor) => brandColor && BRAND_COLOR_PATTERN.test(brandColor) ? brandColor.slice(1) : null;
/* ----------------------------------------------------------------- the session */
/**
 * `POST /v1/public/embed/sessions` — the widget asks for its checkout context after it loads.
 *
 * **Strict**: an amount, a designation or a source in the body is refused with 400 rather than
 * ignored, so nobody can come to believe the widget's address or body can set what is charged.
 *
 * `ancestorOrigin` is **accepted and ignored** (GH-850). It was checked against the allow-list the
 * owner removed; a box loaded from a cached bundle before that change still sends it, and a strict
 * schema that refused it would turn those boxes into errors. Nothing reads it.
 */
export const embedSessionRequestSchema = z
    .object({
    organizationId: z.string().uuid(),
    eventId: z.string().uuid().optional(),
    ancestorOrigin: z.string().max(300).nullable().optional(),
    /**
     * A saved widget of this organization's (GH-851): its stored config decides what the box draws
     * and which event it gives to, and its id rides in the signed context so the quote route can
     * hold the gift to the widget's rules. Another organization's widget reads as not found.
     */
    widgetId: z.string().uuid().optional(),
    /**
     * The console's draft preview (GH-851): a short-lived token a staff session minted for a
     * config that is not saved. It changes no stored widget and the box it draws takes no gift.
     */
    previewToken: z.string().min(1).max(8000).optional(),
})
    .strict();
/**
 * A signed checkout context, held in the widget's memory only — never put in an address — and the
 * organization's brand colour, which tints the box's keyline when the snippet names no tint.
 */
const brandColorWire = z
    .string()
    .regex(/^#[0-9a-f]{6}$/)
    .nullable()
    .default(null);
export const embedSessionReadySchema = z.object({
    /** Absent from an API older than GH-851, which only ever answered ready. */
    state: z.literal("ready").default("ready"),
    token: z.string().min(1),
    expiresAt: z.string(),
    /** `#rrggbb`, or null when the organization has none. Presentation only. */
    brandColor: brandColorWire,
    /** The saved widget (or draft preview) the box draws; null for an org-level snippet. */
    widget: embedSessionWidgetSchema.nullable().default(null),
});
/**
 * A widget the organization archived (GH-851), or whose fixed designation is no longer offered:
 * no context is minted, and the box says the organization is not taking gifts here and links to
 * its page — never an error page on somebody else's site.
 */
export const embedSessionUnavailableSchema = z.object({
    state: z.literal("unavailable"),
    organizationName: z.string(),
    brandColor: brandColorWire,
});
export const embedSessionResponseSchema = z.union([
    embedSessionReadySchema,
    embedSessionUnavailableSchema,
]);
export const embedWidgetLayoutMessage = (layout) => ({
    type: "givehere:widget",
    mode: layout.mode === "popup" ? "popup" : "inline",
    size: ["compact", "standard", "wide", "full"].includes(layout.size)
        ? layout.size
        : "standard",
    buttonLabel: String(layout.buttonLabel).slice(0, 40),
});
/** The frame's height, clamped to what a give box can sensibly be. */
export const EMBED_MIN_HEIGHT = 200;
export const EMBED_MAX_HEIGHT = 4000;
export const embedResizeMessage = (height) => ({
    type: "givehere:resize",
    height: Math.min(EMBED_MAX_HEIGHT, Math.max(EMBED_MIN_HEIGHT, Math.ceil(height))),
});
export const embedCompleteMessage = (status, amount) => ({
    type: "givehere:donation-complete",
    status,
    // Rebuilt field by field so nothing the caller's object happens to carry rides along.
    amount: { amountMinor: String(amount.amountMinor), currency: String(amount.currency) },
});
//# sourceMappingURL=embed.js.map